import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PhotoGrid } from "@/components/records/photo-grid/photo-grid";
import { IssueItem } from "@/components/issues/issue-item/issue-item";
import { useProjectIssues } from "@/hooks/use-issues";
import { useLoadMoreSentinel } from "@/hooks/use-load-more-sentinel";
import { cn } from "@/lib/utils";
import type { CollectionKind, ProjectRecord } from "@/types/api";

/**
 * What the report already holds, which the picker leaves out. Only `ready`
 * once the report page has loaded **all** of it — a set built from the pages
 * scrolled so far would let the picker offer item 61 again.
 */
export type ReportMembership =
  | { status: "loading" }
  | { status: "error"; retry: () => void }
  | { status: "ready"; ids: ReadonlySet<string> };

interface CollectionAddSheetProps {
  projectId: string;
  kind: CollectionKind;
  /** Excluded from the picker so nothing can be added twice. */
  membership: ReportMembership;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (ids: string[]) => void;
  isAdding: boolean;
}

/**
 * The one picker in the feature.
 *
 * It exists so the export flow does not have to carry the choice instead:
 * exporting always makes a *new* report, and topping up an existing one
 * happens here. Items already in the collection are excluded entirely rather
 * than shown ticked, so there is no way to add the same thing twice — and
 * until the whole collection is known, nothing is offered at all. See
 * [[collection-add-sheet.spec]].
 */
export function CollectionAddSheet({
  projectId,
  kind,
  membership,
  open,
  onOpenChange,
  onAdd,
  isAdding,
}: CollectionAddSheetProps) {
  const [selected, setSelected] = useState<string[]>([]);
  // The body is what scrolls, so the issue list watches its end against this
  // rather than the screen. State, not a ref: the list reads it while rendering.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);

  const close = (next: boolean) => {
    if (!next) setSelected([]);
    onOpenChange(next);
  };

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );

  return (
    <Sheet open={open} onOpenChange={close}>
      <SheetContent side="bottom" className="flex h-[85dvh] flex-col p-0">
        <SheetHeader className="border-b">
          <SheetTitle>
            Add {kind === "records" ? "photos" : "issues"}
          </SheetTitle>
          <SheetDescription>
            Everything already in this report is hidden here.
          </SheetDescription>
        </SheetHeader>

        <div ref={setScroller} className="min-h-0 flex-1 overflow-auto p-4">
          {membership.status === "loading" ? (
            <PickerSkeleton kind={kind} />
          ) : membership.status === "error" ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-muted-foreground">
                Couldn&apos;t check what&apos;s already in this report.
              </p>
              <Button variant="outline" size="sm" onClick={membership.retry}>
                Try again
              </Button>
            </div>
          ) : kind === "records" ? (
            <RecordPicker
              projectId={projectId}
              existingIds={membership.ids}
              selected={selected}
              onToggle={toggle}
            />
          ) : (
            <IssuePicker
              projectId={projectId}
              existingIds={membership.ids}
              selected={selected}
              onToggle={toggle}
              scrollRoot={scroller}
            />
          )}
        </div>

        {/* On a phone the bar clears the home indicator and the toast lane. */}
        <SheetFooter className="pb-sheet-actions flex-row gap-2 border-t">
          <Button variant="outline" className="flex-1" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            disabled={selected.length === 0 || isAdding}
            onClick={() => onAdd(selected)}
          >
            {isAdding ? "Adding…" : `Add ${selected.length || ""}`.trim()}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

interface PickerProps {
  projectId: string;
  existingIds: ReadonlySet<string>;
  selected: string[];
  onToggle: (id: string) => void;
}

/**
 * Photos are picked with the project's own grid in selection mode, so the
 * tiles, the pinch-to-resize and the indicators are the ones the user already
 * knows. Records already in the collection are dropped by the grid as its
 * pages arrive — the grid owns its own fetching, and a server-side exclusion
 * would need a filter the records endpoint does not have.
 */
function RecordPicker({
  projectId,
  existingIds,
  selected,
  onToggle,
}: PickerProps) {
  // The whole report, not just the photos scrolled past: every one of them is
  // hidden wherever it falls in the project's paging.
  const hidden = existingIds.size;

  return (
    <>
      {hidden > 0 && (
        <p className="pb-3 text-sm text-muted-foreground">
          {hidden} already in this report {hidden === 1 ? "is" : "are"} hidden.
        </p>
      )}
      <PhotoGrid
        projectId={projectId}
        isSelectionMode
        selectedIds={selected}
        onToggleSelection={(record: ProjectRecord) => onToggle(record.id)}
        excludeIds={existingIds}
        emptyState={
          <p className="py-12 text-center text-sm text-muted-foreground">
            Every photo on this project is already in this report.
          </p>
        }
      />
    </>
  );
}

/**
 * The project's issues a page at a time, the report's own left out as each
 * page arrives. See [[collection-add-sheet.spec]] § Issues load as you scroll.
 */
function IssuePicker({
  projectId,
  existingIds,
  selected,
  onToggle,
  scrollRoot,
}: PickerProps & {
  /** The body the list scrolls inside; the end of the list is watched in it. */
  scrollRoot: Element | null;
}) {
  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useProjectIssues(projectId);
  const loaded = data?.issues ?? [];
  const issues = loaded.filter((i) => !existingIds.has(i.id));

  // A retry keeps the error flag until the page lands, so the failure line is
  // only for a page that is not in flight.
  const pageFailed = isFetchNextPageError && !isFetchingNextPage;
  const canAskForMore = hasNextPage && !isFetchingNextPage && !pageFailed;

  // Everything loaded so far is already in the report. With nothing on screen
  // there is no end to scroll to, so the sentinel would never be reached —
  // keep asking until something is left to show or the project runs out.
  const allExcludedSoFar =
    loaded.length > 0 && issues.length === 0 && hasNextPage;
  useEffect(() => {
    if (allExcludedSoFar && canAskForMore) void fetchNextPage();
    // Re-runs as each page lands: a fast response can start and finish inside
    // one batched update, so the flags alone would not change.
  }, [allExcludedSoFar, canAskForMore, fetchNextPage, loaded.length]);

  const sentinelRef = useLoadMoreSentinel({
    onNearEnd: () => void fetchNextPage(),
    enabled: canAskForMore && scrollRoot !== null,
    loadedCount: loaded.length,
    root: scrollRoot,
  });

  if (isLoading) return <PickerSkeleton kind="issues" />;

  if (issues.length === 0) {
    // "Everything is in the report" is only true once there is no more to ask
    // for; until then the next page may hold something.
    if (!hasNextPage) {
      return (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Every issue on this project is already in this report.
        </p>
      );
    }
    if (!pageFailed) return <PickerSkeleton kind="issues" />;
  }

  return (
    <>
      {issues.length > 0 && (
        <div className="divide-y overflow-hidden rounded-lg border">
          {issues.map((issue) => {
            const isSelected = selected.includes(issue.id);
            return (
              <button
                key={issue.id}
                type="button"
                onClick={() => onToggle(issue.id)}
                className={cn(
                  "flex w-full items-center gap-2 text-left transition-colors",
                  isSelected && "bg-accent/50",
                )}
              >
                <span
                  className={cn(
                    "ml-3 flex size-5 shrink-0 items-center justify-center rounded-full border",
                    isSelected
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-muted-foreground/40",
                  )}
                >
                  {isSelected && <Check className="size-3" />}
                </span>
                <span className="pointer-events-none min-w-0 flex-1">
                  <IssueItem projectId={projectId} issue={issue} />
                </span>
              </button>
            );
          })}
          {isFetchingNextPage && (
            <Skeleton className="h-20 w-full rounded-none" />
          )}
        </div>
      )}
      {pageFailed ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <p className="text-sm text-muted-foreground">
            Couldn&apos;t load more issues.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchNextPage()}
          >
            Try again
          </Button>
        </div>
      ) : (
        hasNextPage && <div ref={sentinelRef} aria-hidden="true" />
      )}
    </>
  );
}

/** Shaped like what is about to load: tiles for photos, rows for issues. */
function PickerSkeleton({ kind }: { kind: CollectionKind }) {
  if (kind === "records") {
    return (
      <div className="grid grid-cols-3 gap-1" aria-busy="true">
        {Array.from({ length: 9 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square w-full" />
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full" />
      ))}
    </div>
  );
}
